import { useSelector } from 'react-redux';

import CountryPreview from 'components/CountryPreview';
import Tags from 'components/Jobseekers/components/Tags';
import ReadMore from 'components/ReadMore/ReadMore';
import getCityStateNameFromLocation from 'components/Reports/RecruitmentOverviewReport/RecruitmentOverviewReportChart/utils/getCityStateNameFromLocation';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import { WorkExperienceRecord } from 'types/commons';

import { Dictionaries } from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import { useTranslation } from 'react-i18next';
import styles from '../WorkExperience.module.scss';

interface WorkExperienceItemProps {
  record: WorkExperienceRecord;
}

const WorkExperienceItem = ({ record }: WorkExperienceItemProps) => {
  const { t } = useTranslation()
  const cityState = getCityStateNameFromLocation(record?.location);

  const jobseekerIndustries = useSelector(
    selectDictionary(Dictionaries.Industry)
  );

  return (
    <div className={styles.experienceWrapper}>
      <div>
        <div className={styles.value}>{record.jobTitle || FALLBACK_VALUE}</div>
        <div className={styles.label}>{t('workExperience.title')}</div>
      </div>
      <div>
        <div className={styles.value}>
          {record.companyName || FALLBACK_VALUE}
        </div>
        <div className={styles.label}>{t('workExperience.eName')}</div>
      </div>
      <div className={styles.dataWrapper}>
        <div className={styles.date}>
          <div className={styles.value}>
            {record.industry ? (
              <Tags
                dictionary={jobseekerIndustries}
                values={[record.industry]}
              />
            ) : (
              FALLBACK_VALUE
            )}
          </div>
          <div className={styles.label}>{t('workExperience.industry')}</div>
        </div>
      </div>
      <div className={styles.dataWrapper}>
        <div className={styles.date}>
          {record?.location?.country ? (
            <CountryPreview
              countryCode={record.location.country}
              className={styles.value}
            />
          ) : (
            FALLBACK_VALUE
          )}
          <div className={styles.label}>{t('workExperience.location')}</div>
        </div>
        <div className={styles.date}>
          <div className={styles.value}>{cityState || FALLBACK_VALUE}</div>
          <div className={styles.label}>{t('workExperience.city')}</div>
        </div>
      </div>
      <div className={styles.dataWrapper}>
        <div className={styles.date}>
          <div className={styles.value}>
            {record.startDate || FALLBACK_VALUE}
          </div>
          <div className={styles.label}>{t('workExperience.start')}</div>
        </div>
        <div className={styles.date}>
          <div className={styles.value}>{record.endDate || FALLBACK_VALUE}</div>
          <div className={styles.label}>{t('workExperience.end')}</div>
        </div>
      </div>
      {record.responsibilities && (
        <ReadMore
          openedText={t('workExperience.hideResp')}
          closedText={t('workExperience.viewResp')}
        >
          <div className={styles.responsibilitiesWrapper}>
            {record.responsibilities.split('\n').map((textLine, index) => (
              <div key={index} className={styles.label}>
                {textLine}
              </div>
            ))}
          </div>
        </ReadMore>
      )}
    </div>
  );
};

export default WorkExperienceItem;
